import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const TerminalCarousel: React.FC = () => {
  const logs = [
    { time: '16:30:00', message: 'SDK initialized successfully', type: 'success' },
    { time: '16:30:00', message: 'Connected to backend server', type: 'info' },
    { time: '16:30:05', message: 'Opening file: Bench_Case.rawx', type: 'info' },
    { time: '16:30:05', message: 'Successfully opened file: Bench_Case.rawx', type: 'success' },
    { time: '16:30:05', message: 'Loading network data...', type: 'info' },
    { time: '16:30:06', message: 'Network data loaded successfully', type: 'success' },
    { time: '16:30:12', message: 'Starting AC Power Flow calculation...', type: 'info' },
    { time: '16:30:12', message: 'AC Power Flow completed successfully in 2 iterations', type: 'success' },
    { time: '16:30:20', message: 'Modifying bus: Bus 14534', type: 'info' },
    { time: '16:30:20', message: 'Successfully modified bus: Bus 14534', type: 'success' },
    { time: '16:30:28', message: 'Modifying generator: Gen 32384, id 1', type: 'info' },
    { time: '16:30:28', message: 'Successfully modified generator: Gen 32384, id 1', type: 'success' },
    { time: '16:30:35', message: 'Saving file: Bench_Case.rawx', type: 'info' },
    { time: '16:30:35', message: 'File saved successfully', type: 'success' },
  ];

  const [visibleLogs, setVisibleLogs] = useState<typeof logs>([]);
  const isResettingRef = useRef(false);
  const loggerContentRef = useRef<HTMLDivElement>(null);

  // Parse time string to seconds
  const parseTime = (timeStr: string): number => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  useEffect(() => {
    let timeoutIds: NodeJS.Timeout[] = [];

    const showLog = (index: number) => {
      if (index >= logs.length) {
        // Reset and loop after all logs shown
        isResettingRef.current = true;
        const resetTimeout = setTimeout(() => {
          setVisibleLogs([]);
          isResettingRef.current = false;
          // Restart after clearing
          const restartTimeout = setTimeout(() => {
            showLog(0);
          }, 500);
          timeoutIds.push(restartTimeout);
        }, 2000);
        timeoutIds.push(resetTimeout);
        return;
      }

      // Show current log
      setVisibleLogs(prev => index === 0 ? [logs[0]] : [...prev, logs[index]]);

      // Calculate delay to next log
      if (index < logs.length - 1) {
        const currentTime = parseTime(logs[index].time);
        const nextTime = parseTime(logs[index + 1].time);
        const timeDiff = nextTime - currentTime;

        // Scale: 1 second in log time = 100ms in animation (10x speed)
        const delay = Math.max(timeDiff * 100, 300);

        const timeout = setTimeout(() => {
          showLog(index + 1);
        }, delay);
        timeoutIds.push(timeout);
      } else {
        // Last log, wait then reset
        const timeout = setTimeout(() => {
          showLog(index + 1);
        }, 1000);
        timeoutIds.push(timeout);
      }
    };

    // Start after initial delay
    const startTimeout = setTimeout(() => {
      showLog(0);
    }, 500);
    timeoutIds.push(startTimeout);

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (loggerContentRef.current) {
      loggerContentRef.current.scrollTop = loggerContentRef.current.scrollHeight;
    }
  }, [visibleLogs]);

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✗';
      case 'warning':
        return '⚠';
      default:
        return '→';
    }
  };

  return (
    <div className="terminal-carousel">
      <div className="panel-header">
        <span className="panel-title">Command Logger</span>
        <div className="panel-controls">
          <button className="panel-btn" title="Fullscreen">
            ⤢
          </button>
        </div>
      </div>
      <div className="panel-content">
        <div className="command-logger">
          <div className="logger-content" ref={loggerContentRef}>
            {visibleLogs.map((log, index) => (
              <div key={index} className={`log-entry log-${log.type}`}>
                <span className="log-timestamp">{log.time}</span>
                <span className="log-icon">{getLogIcon(log.type)}</span>
                <span className="log-message">{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Features: React.FC = () => {
  const { t } = useTranslation();

  const features = [
    {
      id: 1,
      title: t('features.ai.title'),
      description: t('features.ai.description'),
      video: '/videos/automation.mp4',
      poster: '/images/automation.jpg'
    },
    {
      id: 2,
      title: t('features.knowledge.title'),
      description: t('features.knowledge.description'),
      image: '/images/knowledge base.png'
    },
    {
      id: 3,
      title: t('features.modern.title'),
      description: t('features.modern.description'),
      terminal: true
    }
  ];

  return (
    <section id="features" className="features-section">
      <div className="features-container">
        {features.map((feature, index) => (
          <div key={feature.id} className="feature-row">
            <div className="feature-content">
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
            <div className="feature-media-wrapper">
              {feature.video ? (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster={feature.poster}
                >
                  <source src={feature.video} type="video/mp4" />
                </video>
              ) : feature.image ? (
                <img src={feature.image} alt={feature.title} />
              ) : feature.terminal ? (
                <TerminalCarousel />
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Features;
